import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { adminApi } from '@/services/api';
import {
  Users, Activity, TrendingUp, Calendar, Heart, Database,
  Server, Zap, RefreshCw, CheckCircle, XCircle, AlertTriangle,
  DollarSign
} from 'lucide-react';

interface User {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  is_superuser: boolean;
  oauth_provider: string | null;
  created_at: string;
  subscription_tier: string;
  documents_processed_this_month: number;
  api_calls_this_month: number;
  llm_call_count: number;
  total_tokens: number;
  estimated_cost_usd: number;
}

interface LLMCallStat {
  date: string;
  user_id: number;
  email: string;
  full_name: string;
  provider: string;
  model: string;
  call_count: number;
  total_tokens: number;
  avg_duration: number;
  estimated_cost_usd: number;
}

interface LLMStats {
  period_days: number;
  overall: {
    total_calls: number;
    unique_users: number;
    total_tokens: number;
    estimated_cost_usd: number;
  };
  daily_stats: LLMCallStat[];
}

interface HealthStatus {
  status: string;
  database: string;
  redis?: string;
  ollama: string;
  models: {
    fast: { name: string; ready: boolean };
    quality: { name: string; ready: boolean };
    available: string[];
  };
  providers: {
    ollama_fast: boolean;
    ollama_quality: boolean;
    claude: boolean;
    openai: boolean;
    default: string;
  };
  api_keys: {
    anthropic: boolean;
    openai: boolean;
  };
  warning?: string;
}

interface RAGStats {
  total_chunks: number;
  embedding_model: string;
  embedding_dimensions: number;
  vector_db: string;
  storage_path: string;
}

const formatCost = (usd: number) => {
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(2)}`;
  return `$${usd.toFixed(2)}`;
};

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [llmStats, setLlmStats] = useState<LLMStats | null>(null);
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [ragStats, setRagStats] = useState<RAGStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchData();
  }, [days]);

  useEffect(() => {
    fetchHealth();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes] = await Promise.all([
        adminApi.getUsers(),
        adminApi.getLLMStats(days),
      ]);

      setUsers(usersRes.users);
      setLlmStats(statsRes);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || t('admin.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchHealth = async () => {
    setHealthLoading(true);
    try {
      const [healthRes, ragRes] = await Promise.allSettled([
        adminApi.getHealth(),
        adminApi.getRAGStats(),
      ]);

      if (healthRes.status === 'fulfilled') setHealth(healthRes.value);
      if (ragRes.status === 'fulfilled') setRagStats(ragRes.value);
    } catch {
      console.error('Failed to load health data');
    } finally {
      setHealthLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const groupStatsByDate = () => {
    if (!llmStats?.daily_stats) return {};
    const grouped: { [date: string]: LLMCallStat[] } = {};
    for (const stat of llmStats.daily_stats) {
      if (!grouped[stat.date]) grouped[stat.date] = [];
      grouped[stat.date].push(stat);
    }
    return grouped;
  };

  const StatusIndicator = ({ ok, label }: { ok: boolean | string; label: string }) => {
    const isOk = ok === true || ok === 'connected' || ok === 'healthy';
    const isWarning = ok === 'degraded';
    return (
      <div className="flex items-center gap-2">
        {isOk ? (
          <CheckCircle className="h-4 w-4 text-green-500" />
        ) : isWarning ? (
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
        ) : (
          <XCircle className="h-4 w-4 text-red-500" />
        )}
        <span className="text-sm">{label}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('admin.loading')}</p>
        </div>
      </div>
    );
  }

  const groupedStats = groupStatsByDate();
  const totalAllTimeCost = users.reduce((sum, u) => sum + (u.estimated_cost_usd || 0), 0);

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('admin.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('admin.subtitle')}</p>
        </div>
        
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <Button
              key={d}
              variant={days === d ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDays(d)}
            >
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {/* System Health */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Heart className="h-5 w-5" />
            {t('admin.systemHealth')}
          </h2>
          <Button variant="ghost" size="sm" onClick={fetchHealth} disabled={healthLoading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${healthLoading ? 'animate-spin' : ''}`} />
            {t('admin.refresh')}
          </Button>
        </div>

        {health ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Server className="h-4 w-4" />
                  {t('admin.systemStatus')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Badge variant={
                  health.status === 'healthy' ? 'default' :
                  health.status === 'degraded' ? 'secondary' : 'destructive'
                } className={
                  health.status === 'healthy' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' :
                  health.status === 'degraded' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100' : ''
                }>
                  {health.status.toUpperCase()}
                </Badge>
                <StatusIndicator ok={health.database} label={`Database: ${health.database}`} />
                {health.redis && <StatusIndicator ok={health.redis} label={`Redis: ${health.redis}`} />}
                <StatusIndicator ok={health.ollama} label={`Ollama: ${health.ollama}`} />
                {health.warning && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">{health.warning}</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  {t('admin.llmProviders')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <StatusIndicator ok={health.providers.ollama_fast} label={`Ollama Fast (${health.models.fast.name})`} />
                <StatusIndicator ok={health.providers.ollama_quality} label={`Ollama Quality (${health.models.quality.name})`} />
                <StatusIndicator ok={health.providers.claude} label="Claude (Anthropic)" />
                <StatusIndicator ok={health.providers.openai} label="OpenAI" />
                <div className="pt-2 border-t text-xs text-muted-foreground">
                  Default: <span className="font-medium">{health.providers.default}</span>
                  &nbsp;| Keys: Anthropic {health.api_keys.anthropic ? '✓' : '✗'} · OpenAI {health.api_keys.openai ? '✓' : '✗'}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  {t('admin.modelsRag')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {health.models.available?.length > 0 ? (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('admin.availableModels')}</p>
                    <div className="flex flex-wrap gap-1">
                      {health.models.available.map(model => (
                        <Badge key={model} variant="outline" className="text-xs">{model}</Badge>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('admin.noLocalModels')}</p>
                )}

                {ragStats ? (
                  <div className="pt-2 border-t space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('admin.chunks')}</span>
                      <span className="font-medium">{ragStats.total_chunks?.toLocaleString() || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('admin.model')}</span>
                      <span className="font-medium text-xs">{ragStats.embedding_model || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('admin.vectorDb')}</span>
                      <span className="font-medium">{ragStats.vector_db || 'ChromaDB'}</span>
                    </div>
                  </div>
                ) : (
                  <div className="pt-2 border-t">
                    <p className="text-xs text-muted-foreground">{t('admin.ragStatsUnavailable')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : healthLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <Card key={i}>
                <CardContent className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-6 text-center text-muted-foreground">
              {t('admin.healthLoadFailed')}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Overview Cards */}
      <div className="flex flex-wrap gap-4">
        <Card className="w-44">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('admin.totalUsers')}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{users?.length || 0}</div>
            <p className="text-xs text-muted-foreground">
              {users?.filter(u => u.is_active).length || 0} {t('admin.active')}
            </p>
          </CardContent>
        </Card>

        <Card className="w-44">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('admin.llmCalls')}</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{llmStats?.overall?.total_calls || 0}</div>
            <p className="text-xs text-muted-foreground">{t('admin.lastDays', { days })}</p>
          </CardContent>
        </Card>

        <Card className="w-44">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('admin.activeUsers')}</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{llmStats?.overall?.unique_users || 0}</div>
            <p className="text-xs text-muted-foreground">{t('admin.madeLlmCalls')}</p>
          </CardContent>
        </Card>

        <Card className="w-44">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('admin.tokens')}</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {((llmStats?.overall?.total_tokens || 0) / 1000).toFixed(1)}K
            </div>
            <p className="text-xs text-muted-foreground">{t('admin.lastDays', { days })}</p>
          </CardContent>
        </Card>

        <Card className="w-44">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('admin.estimatedCost')}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCost(llmStats?.overall?.estimated_cost_usd || 0)}
            </div>
            <p className="text-xs text-muted-foreground">{t('admin.lastDays', { days })}</p>
          </CardContent>
        </Card>

        <Card className="w-44">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('admin.totalCost')}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCost(totalAllTimeCost)}</div>
            <p className="text-xs text-muted-foreground">{t('admin.allTime')}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('admin.totalUsers')}
            </CardTitle>
            <CardDescription>{t('admin.usersDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {users?.map(user => (
                <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{user.full_name || user.email}</p>
                      {user.is_superuser && (
                        <Badge variant="default" className="text-xs shrink-0">{t('admin.admin')}</Badge>
                      )}
                      {!user.is_active && (
                        <Badge variant="destructive" className="text-xs shrink-0">{t('admin.inactive')}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground">
                      <span>{t('admin.tier')}: {user.subscription_tier}</span>
                      <span>{t('admin.docs')}: {user.documents_processed_this_month}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="text-sm font-medium">
                      {(user.llm_call_count || 0).toLocaleString()} {t('admin.calls')}
                    </div>
                    <div className="text-sm font-medium text-green-600 dark:text-green-400">
                      {formatCost(user.estimated_cost_usd || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatDate(user.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Daily LLM Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              {t('admin.recentCalls')}
            </CardTitle>
            <CardDescription>{t('admin.callsPerUser')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {Object.entries(groupedStats).map(([date, stats]) => {
                const dayCost = stats.reduce((s, r) => s + (r.estimated_cost_usd || 0), 0);
                return (
                  <div key={date} className="border-b pb-3 last:border-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">{formatDate(date)}</h4>
                      <div className="flex gap-3 text-sm text-muted-foreground">
                        <span>{stats.reduce((s, r) => s + r.call_count, 0)} {t('admin.calls')}</span>
                        <span className="text-green-600 dark:text-green-400 font-medium">{formatCost(dayCost)}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {stats.map((stat, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm pl-4">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium truncate">{stat.full_name || stat.email}</span>
                            <Badge variant="outline" className="text-xs shrink-0">{stat.model || stat.provider}</Badge>
                          </div>
                          <div className="flex gap-3 text-xs text-muted-foreground shrink-0 ml-2">
                            <span>{stat.call_count} {t('admin.calls')}</span>
                            {stat.total_tokens > 0 && (
                              <span>{(stat.total_tokens / 1000).toFixed(1)}K tok</span>
                            )}
                            <span className="text-green-600 dark:text-green-400">
                              {formatCost(stat.estimated_cost_usd || 0)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              
              {Object.keys(groupedStats).length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  {t('admin.noLlmCalls', { days })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
